import { useCallback, useEffect, useId, useRef, useState } from 'react';
import isInsidePortalLayer from '../../utils/isInsidePortalLayer';

// ============================================================================
// PAYLAŞILAN DİYALOG ERİŞİLEBİLİRLİĞİ (FE-25 / FE-40)
// ============================================================================
// NEDEN VAR: ATS'in diyalogları elle yazılmış perde + kart ikilileriydi. ESC
// çalışmıyordu, Tab odağı diyaloğun ARKASINA kaçıyordu (arkadaki tablonun
// satırlarında geziyordu), kapanışta odak hiçbir yere dönmüyordu ve perdeye
// yanlışlıkla dokunmak 10 alanlık teklif formunu SİLİYORDU
// (`OfferCreateDialog`/`InterviewScheduleDialog`/`ScorecardFillDialog`/
// `ScorecardTemplateDialog`'daki `<Overlay onClick={onClose}>`).
//
// Tarif YENİ DEĞİL: `components/ui/DocumentViewerModal.js` bu odak tuzağını
// zaten taşıyordu (ve kendi test süitiyle çivilenmişti). Burada o kanıtlanmış
// tarif TEK kopyaya çıkarıldı; DocumentViewerModal da artık bunu tüketiyor,
// yani mevcut testleri bu modülün gerileme kapısı.
//
// TASARIM KARARLARI (raporda gerekçeleriyle):
//  1. DIŞARI TIKLAMA HİÇBİR FORM DİYALOĞUNU KAPATMAZ. "Onay sor" seçeneği de
//     vardı; iki sebeple reddedildi: (a) ATS'in 17 diyaloğundan 13'ü zaten
//     kapanmıyordu — hepsini "onay sor"a çevirmek, bugün veri kaybetmeyen 13
//     yüzeye YENİ bir kesinti eklerdi; (b) perdeye tıklamak kullanıcının
//     "kapat" niyetini taşımaz (kaydırırken, seçici kapatırken, boşluğa
//     dokunurken olur) — niyetsiz bir jeste onay diyaloğuyla karşılık vermek
//     gürültüdür. ESC ve İptal düğmesi niyetlidir; kapanış yolu onlardır.
//     `closeOnOutsideClick` bayrağı VERİ TAŞIMAYAN kabuklar içindir
//     (DocumentViewerModal, ConfirmDialog) — davranışları değişmesin diye.
//  2. ESC her zaman kapatma İSTEĞİDİR; form kirliyse (`dirty`) diyaloğun İÇİNDE
//     "değişiklikleri at / vazgeç" sorulur. `window.confirm` KULLANILMAZ (ev
//     kuralı) ve toast KULLANILMAZ (toast yalnız interceptor'da).
//  3. Atma onayı açıkken ESC onayı İPTAL eder, diyaloğu kapatmaz: tekrarlanan
//     bir tuş vuruşu asla veri kaybettirmemeli.
// ============================================================================

// role="dialog" içindeki, klavyeyle ulaşılabilen elemanlar — Tab döngüsü bunun
// üzerinden hesaplanır. `disabled` olanlar kasıtlı dışlanır: kullanıcı onlara
// Tab ile ULAŞAMAMALI.
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Perde arkası ekran okuyucudan gizlenirken ATLANACAK yüzeyler.
// - Canlı bölgeler (sonner toast'ları, role="alert"/"status"): bir diyalog
//   açıkken gelen hata mesajı ekran okuyucuya ULAŞMALI. `aria-hidden` konursa
//   interceptor'ın tek hata kanalı sessizleşir.
// - Render edilmeyen etiketler: `aria-hidden` anlamsız, DOM'u kirletir.
const NEVER_HIDE_SELECTOR = [
  '[data-sonner-toaster]',
  '[aria-live]',
  '[role="alert"]',
  '[role="status"]',
].join(', ');
const NEVER_HIDE_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'TEMPLATE', 'HEAD', 'TITLE', 'BASE']);

function shouldHide(node) {
  if (!node || node.nodeType !== 1) return false;
  if (NEVER_HIDE_TAGS.has(node.tagName)) return false;
  if (typeof node.matches === 'function' && node.matches(NEVER_HIDE_SELECTOR)) return false;
  return true;
}

/**
 * Verilen elemanın DIŞINDA kalan her şeyi ekran okuyucudan gizler.
 *
 * Diyaloglarımız `createPortal` KULLANMIYOR — sayfa ağacının içinde, kardeş bir
 * düğüm olarak duruyorlar. Bu yüzden "kökü `aria-hidden` yap" işe yaramaz
 * (diyaloğun kendisi de gizlenir). Doğru algoritma: elemandan `document.body`'ye
 * kadar yürü ve HER ata seviyesinde ata-ZİNCİRİNDE OLMAYAN kardeşleri gizle.
 * Portal'a taşınmış bir diyalogda da aynı yürüyüş doğru çalışır.
 *
 * İç içe diyalog (ör. bir `ModalCard`'ın üstündeki `ConfirmDialog`) doğru
 * davranır: alttaki diyalog da gizlenir, kapanışta ÖNCEKİ değer geri yazılır.
 *
 * @returns {() => void} geri alma fonksiyonu
 */
export function hideOthersFromScreenReaders(element) {
  if (!element || typeof element.parentNode === 'undefined') return () => {};
  const changed = [];
  let node = element;
  while (node && node.parentNode && node.parentNode.nodeType === 1) {
    // `current` döngü içinde YENİDEN bağlanan `node`'un o turdaki sabit kopyası:
    // aşağıdaki callback `node`u kapatsaydı (no-loop-func) bir sonraki turda
    // değişmiş değeri görürdü.
    const current = node;
    const parent = current.parentNode;
    Array.prototype.forEach.call(parent.children, (sibling) => {
      if (sibling === current || !shouldHide(sibling)) return;
      changed.push([sibling, sibling.getAttribute('aria-hidden')]);
      sibling.setAttribute('aria-hidden', 'true');
    });
    node = parent;
  }
  return () => {
    changed.forEach(([el, previous]) => {
      if (previous === null) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', previous);
    });
  };
}

// AÇIK DİYALOG YIĞINI — iç içe diyaloglarda ESC ve Tab'ın SAHİBİNİ belirler.
// Dinleyiciler `document` üzerinde durduğu için `stopPropagation` bir işe yaramaz
// (aynı düğümdeki diğer dinleyiciler yine çalışır). Somut vaka: skorkart doldurma
// diyaloğunun içinden açılan CV önizleyicisi — ESC'e basınca ÖNİZLEYİCİ kapanmalı,
// altındaki doldurulmuş skorkart AÇIK KALMALIDIR.
const dialogStack = [];
let dialogSeq = 0;

/**
 * Bir modal diyaloğun klavye/odak/ekran-okuyucu sözleşmesi.
 *
 * @param {object}   opts
 * @param {boolean}  opts.open                 diyalog açık mı
 * @param {Function} opts.onClose              kapatma (veri kaybı riski YOKSA doğrudan çağrılır)
 * @param {boolean}  opts.dirty                kullanıcı veri girdi mi (ESC'te onay sorulur)
 * @param {boolean}  opts.closeOnOutsideClick  perdeye tıklama kapatsın mı (varsayılan: HAYIR)
 * @param {'first'|'container'} opts.initialFocus  açılışta odak nereye gitsin
 */
export default function useDialogA11y({
  open,
  onClose,
  dirty = false,
  closeOnOutsideClick = false,
  initialFocus = 'first',
} = {}) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const dirtyRef = useRef(dirty);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const discardPromptRef = useRef(false);
  const reactId = useId();
  const titleId = `dlg-title-${reactId}`;
  const stackIdRef = useRef(null);
  if (stackIdRef.current === null) { dialogSeq += 1; stackIdRef.current = dialogSeq; }

  onCloseRef.current = onClose;
  dirtyRef.current = dirty;
  discardPromptRef.current = discardPrompt;

  // Diyalog kapanınca atma onayı da sıfırlanmalı; aksi halde diyalog bir daha
  // açıldığında onay ekranıyla açılır.
  useEffect(() => { if (!open) setDiscardPrompt(false); }, [open]);

  const closeNow = useCallback(() => {
    setDiscardPrompt(false);
    onCloseRef.current?.();
  }, []);

  // Kapatma İSTEĞİ — kirli formda önce onay sorar.
  const requestClose = useCallback(() => {
    if (dirtyRef.current) { setDiscardPrompt(true); return; }
    closeNow();
  }, [closeNow]);

  const cancelDiscard = useCallback(() => setDiscardPrompt(false), []);

  // AÇILIŞTA: odağı sakla, diyaloğun İÇİNDEKİ ilk odaklanabilir öğeye geç.
  // KAPANIŞTA: odağı AÇAN düğmeye geri ver.
  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog) {
      // Form diyaloglarında odak İLK ALANA gider: kullanıcı yazmaya hemen
      // başlayabilsin. İçerik kabuklarında (`initialFocus: 'container'` —
      // belge önizleyicisi) kabın kendisine gider: orada ilk odaklanabilir
      // öğe "İndir"dir ve odağı ona vermek, ekran okuyucuya belgenin
      // başlığından önce bir düğme okutur; ayrıca ok tuşlarıyla kaydırma
      // kabın odakta olmasını gerektirir.
      const first = initialFocus === 'container' ? null : dialog.querySelector(FOCUSABLE_SELECTOR);
      // İlk odaklanabilir öğe yoksa kabın kendisi (tabIndex=-1) odaklanır —
      // odak yine de diyaloğun İÇİNDE başlar, arkadaki sayfada değil.
      if (first && typeof first.focus === 'function') first.focus();
      else if (typeof dialog.focus === 'function') dialog.focus();
    }
    return () => {
      const toRestore = previousFocusRef.current;
      if (toRestore && typeof toRestore.focus === 'function' && document.contains(toRestore)) {
        toRestore.focus();
      }
    };
  }, [open, initialFocus]);

  // Perde arkasını ekran okuyucudan gizle.
  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;
    return hideOthersFromScreenReaders(dialogRef.current);
  }, [open]);

  // Yığına gir/çık. En SONA giren diyalog klavyenin sahibidir.
  useEffect(() => {
    if (!open) return undefined;
    const id = stackIdRef.current;
    dialogStack.push(id);
    return () => {
      const at = dialogStack.lastIndexOf(id);
      if (at !== -1) dialogStack.splice(at, 1);
    };
  }, [open]);

  // ESC + Tab tuzağı.
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      // Üstte başka bir diyalog varsa klavye onundur.
      if (dialogStack[dialogStack.length - 1] !== stackIdRef.current) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        // Atma onayı açıkken ESC ONAYI iptal eder — ikinci bir ESC formu
        // silmemeli (karar 3).
        if (discardPromptRef.current) { setDiscardPrompt(false); return; }
        if (dirtyRef.current) { setDiscardPrompt(true); return; }
        closeNow();
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      // Atma onayı açıkken döngü ONAYIN içinde dönmeli: arkadaki form alanları
      // artık ulaşılamaz olmalı, yoksa kullanıcı "kapatayım mı" sorusunu
      // yanıtlamadan forma geri Tab'lar.
      const trapRoot = dialog.querySelector('[data-dialog-trap-root]') || dialog;
      const focusable = Array.from(trapRoot.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      // `active === trapRoot` ya da odak tuzağın tamamen dışında: sınırı ZORLA.
      // jsdom (ve bazı tarayıcılar) doğal Tab davranışını uygulamaz; zorlanmazsa
      // döngü kırılır ve odak arkadaki sayfaya kaçar.
      const atBoundary = active === trapRoot || !trapRoot.contains(active);
      if (event.shiftKey) {
        if (atBoundary || active === first) { event.preventDefault(); last.focus(); }
      } else if (atBoundary || active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeNow]);

  // Perde tıklaması. `mousedown` kullanılır, `click` DEĞİL: bir seçicinin açılır
  // listesi üzerinde basılıp liste kapanınca `click` perdede doğar ve "dışarı"
  // sanılır (repo hafızası `reference_portal_disari_tiklama_tuzagi`).
  //
  // PORTAL TUZAĞI BURADA NASIL KAPANIYOR — dürüst kayıt. Yükü taşıyan kapı
  // `event.target !== event.currentTarget`tir. `createPortal` düğümü
  // document.body'ye basılsa da React sentetik olayı REACT ağacında yukarı
  // taşıdığı için mousedown perdeye GERÇEKTEN ulaşır; ama o anda `target`
  // açılır listenin düğümü, `currentTarget` perdedir — eşit olmadıkları için
  // erken dönülür (`dialogA11y.test.js` bunu gerçek bir `createPortal` ile
  // doğruluyor; kapıyı kaldıran mutasyon KIRMIZI).
  //
  // Alttaki `isInsidePortalLayer` bu kurulumda ULAŞILAMAZ: bir portal düğümü
  // perdenin KENDİSİ olamaz. Mutasyon koşumunda silindiğinde hiçbir test
  // kırılmadı — EŞDEĞER mutasyon, zayıf test değil. Yine de duruyor: kapı
  // ileride "kartın içinde mi" (`cardRef.contains`) biçimine gevşetilirse —
  // ki yaygın alternatif odur — portal tıklaması bir anda "dışarı" sayılır ve
  // bu satır tek savunma hattı olur.
  const handleOverlayMouseDown = useCallback((event) => {
    if (event.target !== event.currentTarget) return;     // kartın İÇİ / portal katmanı
    if (isInsidePortalLayer(event.target)) return;        // savunma derinliği (bkz. yukarısı)
    if (!closeOnOutsideClick) return;                     // KARAR 1: kapatma
    requestClose();
  }, [closeOnOutsideClick, requestClose]);

  return {
    dialogRef,
    titleId,
    discardPrompt,
    requestClose,
    cancelDiscard,
    confirmDiscard: closeNow,
    overlayHandlers: { onMouseDown: handleOverlayMouseDown },
    dialogProps: {
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      tabIndex: -1,
    },
  };
}
